import { takeEvery } from "@redux-saga/core/effects";
import { call, put, select } from "redux-saga/effects";
import { setAppPastIntro } from "../Actions/AppActions";
import { setSearchedMatchPlace } from "../Actions/RetailMatchActions";
import { setSideNavOpen } from "../Actions/SideNavActions";
import * as api from "../API/CommonApi";

import * as types from "../Types/PlaceSearchTypes";
import {
    onLocationSelectedForAnalysis,
    setInProcessPolygon,
    setIsLoading,
    setIsLoadingLocations,
    setIsViewingLocationDetails,
    setLocationDetails,
    setScenarioFormStates,
    setSearchedMobilyticsPlace
} from "../Actions/MobilyticsActions";
import { App } from "../Enums/App";
import { currentAppSelector } from "../Selectors/AppSelectors";
import { doPlaceSearch } from "../Actions/PlaceSearchActions";
import { getFirstGeocodeResult, SupportedGeocoderCountry } from "../Util/GeocoderUtil";
import { mapMarkersSelector } from "../Selectors/MapSelectors";
import { setMapMarkers } from "../Actions/MapActions";
import { IMapMarker, MapMarker } from "../Models/IMapMarker";
import { circleFromQueryParams, customPolygonFromPoints, getCentroid } from "../Logic/PolygonLogic";
import { ICircleQueryParams } from "../Models/ICircleQueryParams";
import { IMobilyticsPlaceQueryParams } from "../Models/IMobilyticsPlaceQueryParams";
import { IPolygonQueryParams } from "../Models/IPolygonQueryParams";
import { defaultScenarioFormState, IMobilyticsScenarioFormState } from "../Pages/Apps/Mobilytics/SubmitMobilyticsLogic";
import { getSafeGraphLocations } from "../Logic/MobilyticsLogic";
import { IPolygon } from "../Models/Maps/IPolygon";
import { LocationDetailsParameters } from "../Types/MobilyticsActionTypes";
import { scenarioFormStatesSelector } from "../Selectors/MobilyticsSelectors";
import { MAXIMUM_SCENARIOS } from "./MobilyticsSagas";
import { MobilyticsQueryParam } from "../Models/MobilyticsQueryParam";
import { usaWKTSelector } from "../Selectors/CommonSelectors";
import { setUSAWKT } from "../Actions/CommonActions";
import { IServiceResult } from "../../../Models/IServiceResult";

export function* watchPlaceSearchSagas() {
    yield takeEvery(types.DO_PLACE_SEARCH, doPlacesSearch);
    yield takeEvery(types.DO_LATLNG_SEARCH, doLatLngSearch);
    yield takeEvery(types.DO_LOAD_LOCATIONS, loadLocations);
}

const placesService = new google.maps.places.PlacesService(document.createElement('div'));
const geocoderService = new google.maps.Geocoder();

const isWktEmpty = (wkt: string) => !wkt?.trim();

export const getGeocodeFromLatLng = (lat: number, lng: number): Promise<google.maps.GeocoderResult[]> => {
    return new Promise((resolve, reject) => {
        geocoderService.geocode({ location: { lat: lat, lng: lng }}, (result, status) => {
            if (status === "OK") {
                resolve(result);
            } else {
                reject(status);
            }
        });
    });
};

export const getPlaceDetails = (
    placeId: string,
    sessionToken: google.maps.places.AutocompleteSessionToken
) : Promise<google.maps.places.PlaceResult> => {
    return new Promise((resolve, reject) => {
        placesService.getDetails({
            sessionToken,
            placeId,
            //fields: ["geometry.location", "formatted_address", "name", "types"],
        }, (result, status) => {
            if (status === google.maps.places.PlacesServiceStatus.OK) {
                resolve(result);
            } else {
                reject(status);
            }
        });
    });
};

export const getNearbyLocations = (
    name: string,
    lat: number,
    lng: number,
    types: string[],
) : Promise<google.maps.places.PlaceResult[]> => {
    return new Promise((resolve, reject) => {
        placesService.nearbySearch({
            name,
            location: {lat, lng},
            types,
            rankBy: google.maps.places.RankBy.DISTANCE,
        }, (result, status) => {
            if (status === google.maps.places.PlacesServiceStatus.OK) {
                resolve(result);
            } else {
                reject(status);
            }
        });
    });
};

export const searchAutocompleteWithLatLng = (input: string, lat: number, lng: number): Promise<Array<google.maps.places.AutocompletePrediction>> => {
    return new Promise<Array<google.maps.places.AutocompletePrediction>>((resolve, reject) => {
        const sessionToken = new google.maps.places.AutocompleteSessionToken();
        const autoCompleteService = new google.maps.places.AutocompleteService();
        const componentRestrictions = {
            country: 'us',
        };
        const location = new google.maps.LatLng(lat, lng);
        const radius = 100;
        autoCompleteService.getPlacePredictions({ input, sessionToken, location, radius, componentRestrictions }, (result, status) => {
            if (status === google.maps.places.PlacesServiceStatus.OK) {
                resolve(result);
            } else {
                reject(status);
            }
        });
    });
};

function* doPlacesSearch(action: types.DoPlaceSearchAction) {
    try {

        const {
            sessionToken,
            app,
            placeId,
        } = action.data;

        const usaWKT = yield select(usaWKTSelector);
        if (isWktEmpty(usaWKT)) {
            const result: IServiceResult<string> = yield call(() =>
                api.getCountryWKT("USA")
            );
            yield put(setUSAWKT(result.data));
        }

        const details = yield getPlaceDetails(placeId, sessionToken);

        const marker = new MapMarker(
            placeId,                            // id
            details.geometry.location.lat(),    // lat
            details.geometry.location.lng(),    // lng
            '',                                 // icon name
            true,                               // selected
            false,                              // loading
            42,                                 // width
            42,                                 // height
            '',                                 // content
            false,                              // info window open
            details.formatted_address,          // name
            false,                              // default marker
        );
        
        if(app === App.Mobilytics) {
            const scenarios = yield select(scenarioFormStatesSelector);
            //if there are two scenarios, do not add a marker            
            if(scenarios?.length !== 2) {
                const existingMarkers: IMapMarker[] = yield select(mapMarkersSelector);
                //if there is a scenario and scenario marker, only remove the most recent marker added which is not the scenario marker
                if(scenarios.length === 1 && existingMarkers.length > 1) {
                    //remove the most recently added marker
                    existingMarkers.pop();
                }
                
                //if the user does not have a scenario yet, only let them add one marker until they add a scenario
                if(scenarios.length < 1) {
                    existingMarkers.splice(0, existingMarkers.length);
                }

                yield put(setMapMarkers([...existingMarkers, marker]));
            }
        }
        
        if(app === App.Match) {
            //can only have a single scenario in match, so we need to remove all markers before adding a new scenario            
            yield put(setMapMarkers([marker]));
        }

        
        yield put(setAppPastIntro(app));
        yield put(setLocationDetails({
            name: details.name,
            placeId,
            lat: details.geometry.location.lat(),
            lng: details.geometry.location.lng(),
        }));
        if (app === App.Match) {
            yield put(setSearchedMatchPlace(details.formatted_address, placeId, details.geometry.location.lat(),  details.geometry.location.lng()));
        } else if (app === App.Mobilytics) {
            yield put(onLocationSelectedForAnalysis({  placeId, sessionToken }));
            yield put(setSearchedMobilyticsPlace(placeId, details.formatted_address));
            yield put(setInProcessPolygon(null));
        }
        yield put(setSideNavOpen(true));
    } catch (e) {
        console.log(e);
        // todo: Handle failure
    }
};

function* doLatLngSearch(action: types.DoLatLngSearchAction) {
    const lat = action.data.lat;
    const lng = action.data.lng;
    const latTrunc = Math.round(lat * 10000) / 10000;
    const lngTrunc = Math.round(lng * 10000) / 10000;
    const latLngName = `(${latTrunc}, ${lngTrunc})`;
 
    const currentApp = yield select(currentAppSelector);

    if (currentApp === App.Mobilytics) {
        const places = yield getGeocodeFromLatLng(lat, lng);
        const place = getFirstGeocodeResult(places, SupportedGeocoderCountry.US);
        if (place) {
            const sessionToken = new google.maps.places.AutocompleteSessionToken();
            yield put(setIsViewingLocationDetails(false));
            yield put(setLocationDetails(null));
            yield put(doPlaceSearch(currentApp, place.place_id, sessionToken));
        }
    } else if (currentApp === App.Match) {
        const places = yield getGeocodeFromLatLng(lat, lng);
        const place = getFirstGeocodeResult(places, SupportedGeocoderCountry.US);
        if (place) {
            const sessionToken = new google.maps.places.AutocompleteSessionToken();
            yield put(doPlaceSearch(currentApp, place.place_id, sessionToken));
        }
    } else {
        yield put(setSearchedMatchPlace(latLngName, 'none', lat, lng));
        yield put(setSideNavOpen(true));
    }
};

/** 
 * Begin Type guards
 */
const paramsRepresentCircle = (param: MobilyticsQueryParam) : param is ICircleQueryParams => {
    const circle = param as ICircleQueryParams;
    return !!circle.latitude && !!circle.longitude && !!circle.radiusMeters;
};

const paramsRepresentCustomPolygon = (param: MobilyticsQueryParam) : param is IPolygonQueryParams => {
    const polygon = param as IPolygonQueryParams;
    return polygon?.points?.length > 0;
};

const paramsRepresentMobilyticsPlace = (param: MobilyticsQueryParam) : param is IMobilyticsPlaceQueryParams => {
    const place = param as IMobilyticsPlaceQueryParams;
    return place?.safegraphLocationId?.length > 0;
};
/** 
 * End Type guards
 */

function* loadLocations(action: types.DoLoadLocationsAction) {
    try {
        const { locations } = action.data;

        yield put(setIsLoadingLocations(true));

        const scenarioFormStates = yield(select(scenarioFormStatesSelector));
        if ((scenarioFormStates.length + locations.length) > MAXIMUM_SCENARIOS) {
            return yield put(setIsLoadingLocations(false));
        }

        const usaWKT = yield select(usaWKTSelector);
        if (isWktEmpty(usaWKT)) {
            const result: IServiceResult<string> = yield call(() =>
                api.getCountryWKT("USA")
            );
            yield put(setUSAWKT(result.data));
        }

        const scenarios: IMobilyticsScenarioFormState[] = [];
        let firstLocationIsPlace = false;
        const firstLocationDetails: LocationDetailsParameters = {
            name: null,
            placeId: null,
            lat: null,
            lng: null,
        };

        yield put(setIsLoading(true));

        for (let o = 0; o < locations.length; o++) {
            const location = locations[o];

            let title = "";
            let polygon: IPolygon;
            let safegraphLocationId: string;
            let safegraphBrandId: string;

            if (paramsRepresentCircle(location)) {
                polygon = circleFromQueryParams(location.latitude, location.longitude, location.radiusMeters);
                title = location.title;
            } else if (paramsRepresentCustomPolygon(location)) {
                polygon = customPolygonFromPoints(location.points);
                title = location.title;
            } else if (paramsRepresentMobilyticsPlace(location)) {
                const sgLocations = yield getSafeGraphLocations([location.safegraphLocationId]);
                const sgLocation = sgLocations?.length > 0 ? sgLocations[0] : null;
                polygon = customPolygonFromPoints(sgLocation.polygon);
                title = location.title;
                safegraphLocationId = sgLocation.locationId;
                safegraphBrandId = sgLocation.brandId;
                if (o === 0) {
                    firstLocationIsPlace = true;
                }
            }

            const scenario: IMobilyticsScenarioFormState = {
                ...defaultScenarioFormState,
                isCustomPolygon: true,
                polygon,
                title,
            };

            const centroid = getCentroid(polygon);
            const places = yield getGeocodeFromLatLng(centroid.lat(), centroid.lng());
            const place = getFirstGeocodeResult(places, SupportedGeocoderCountry.US);

            if (place) {
                const placeId = place.place_id;
                const sessionToken = new google.maps.places.AutocompleteSessionToken();
                const details = yield getPlaceDetails(placeId, sessionToken);
                if (o === 0) {
                    firstLocationDetails.lat = details.geometry.location.lat();
                    firstLocationDetails.lng = details.geometry.location.lng();
                    firstLocationDetails.placeId = placeId;
                    firstLocationDetails.name = title;
                }

                scenario.lat = details.geometry.location.lat();
                scenario.lng = details.geometry.location.lng();
                scenario.placeId = placeId;

                if (safegraphLocationId) {
                    scenario.locationId = safegraphLocationId;
                }

                if (safegraphBrandId) {
                    scenario.brandId = safegraphBrandId;
                }

                yield put(onLocationSelectedForAnalysis({  placeId, sessionToken, hasPolygon: true }));
                yield put(setSearchedMobilyticsPlace(placeId, details.formatted_address));
                yield put(setSideNavOpen(true));
            }

            scenarios.push(scenario);
        }

        yield put(setAppPastIntro(App.Mobilytics));

        const currentScenarioFormStates = yield(select(scenarioFormStatesSelector));
        yield put(setScenarioFormStates([...currentScenarioFormStates, ...scenarios]));
        yield put(setSideNavOpen(true));
        yield put(setLocationDetails({
            ...firstLocationDetails
        }));
        yield put(setIsLoading(false));
        yield put(setIsLoadingLocations(false));
    } catch (e) {
        console.log(e);
        // todo: Handle failure
    }
}